import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase';

const { FiTrendingUp, FiMousePointer, FiNavigation, FiArrowLeft, FiActivity, FiGlobe, FiTarget } = FiIcons;
const { FaWaze, FaGoogle, FaApple } = FaIcons;

const Dashboard = () => {
  const [stats, setStats] = useState({ totalEvents: 0, topFeatures: [], navPrefs: [], recentEvents: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
    
    // Subscribe to real-time changes
    const channel = supabase
      .channel('live_analytics_dashboard')
      .on('postgres_changes', { event: 'INSERT', table: 'user_events_1740500000000' }, (payload) => {
        // Optimistically add new event to the list
        setStats(prev => ({
          ...prev,
          totalEvents: prev.totalEvents + 1,
          recentEvents: [payload.new, ...prev.recentEvents].slice(0, 8)
        }));
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchAnalytics = async () => {
    try {
      const { data: events, error } = await supabase
        .from('user_events_1740500000000')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500); // Limit to recent 500 for performance

      if (error) throw error;
      if (!events) return;

      const actionMap = {};
      const navMap = { google: 0, waze: 0, apple: 0 };

      events.forEach(ev => {
        actionMap[ev.event_name] = (actionMap[ev.event_name] || 0) + 1;
        if (ev.event_name && ev.event_name.includes('Launch Navigator')) {
          const lowerName = ev.event_name.toLowerCase();
          if (lowerName.includes('google')) navMap.google++;
          else if (lowerName.includes('waze')) navMap.waze++;
          else if (lowerName.includes('apple')) navMap.apple++;
        }
      });

      setStats({
        totalEvents: events.length,
        topFeatures: Object.entries(actionMap)
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 6),
        navPrefs: [
          { label: 'Google', count: navMap.google, icon: FaGoogle, color: 'text-red-500' },
          { label: 'Waze', count: navMap.waze, icon: FaWaze, color: 'text-blue-400' },
          { label: 'Apple', count: navMap.apple, icon: FaApple, color: 'text-slate-900' }
        ],
        recentEvents: events.slice(0, 8)
      });
    } catch (err) {
      console.error('Dash Error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="font-black text-[10px] uppercase tracking-[0.3em] text-slate-400">Loading Intelligence...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 px-6 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <Link to="/" className="flex items-center gap-2 text-indigo-600 font-black text-[10px] uppercase tracking-widest mb-4 hover:translate-x-[-4px] transition-transform">
              <SafeIcon icon={FiArrowLeft} /> Back to App
            </Link>
            <h1 className="text-4xl font-black text-slate-950 tracking-tighter">Behavior <span className="text-indigo-600">Summary</span></h1>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-2">Live Clickstream Analysis</p>
          </div>
          <div className="hidden sm:block text-right">
            <div className="bg-indigo-50 text-indigo-600 px-6 py-3 rounded-2xl border border-indigo-100 inline-block">
              <p className="text-[8px] font-black uppercase tracking-widest leading-none mb-1">Total Interactions</p>
              <p className="text-2xl font-black leading-none">{stats.totalEvents}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 -mt-8 space-y-8">
        {/* Engagement Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Feature Heatmap */}
          <div className="bg-slate-950 rounded-[2.5rem] p-8 text-white shadow-2xl relative overflow-hidden">
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-8">
                <div className="p-3 bg-indigo-600 rounded-2xl shadow-lg shadow-indigo-500/20">
                  <SafeIcon icon={FiMousePointer} className="text-xl" />
                </div>
                <h3 className="text-xl font-black tracking-tight uppercase">Top Engagement</h3>
              </div>
              
              <div className="space-y-6">
                {stats.topFeatures.length > 0 ? stats.topFeatures.map((f, i) => (
                  <div key={i} className="space-y-2">
                    <div className="flex justify-between items-end">
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 truncate max-w-[220px]">{f.name}</span>
                      <span className="text-xs font-black text-indigo-400">{f.count}</span>
                    </div>
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                      <motion.div 
                        initial={{ width: 0 }} 
                        animate={{ width: `${(f.count / stats.topFeatures[0].count) * 100}%` }}
                        className="h-full bg-indigo-500" 
                      />
                    </div>
                  </div>
                )) : (
                  <p className="text-slate-500 text-xs text-center py-4">No data yet</p>
                )}
              </div>
            </div>
            <SafeIcon icon={FiTrendingUp} className="absolute -bottom-10 -right-10 text-[12rem] text-white/5" />
          </div>

          {/* Navigator Market Share */}
          <div className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-xl">
            <div className="flex items-center gap-3 mb-8">
              <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100">
                <SafeIcon icon={FiNavigation} className="text-xl text-indigo-600" />
              </div>
              <h3 className="text-xl font-black tracking-tight uppercase text-slate-950">Navigator Choice</h3>
            </div>

            <div className="grid grid-cols-3 gap-4">
              {stats.navPrefs.map((nav, i) => (
                <div key={i} className="text-center p-4 rounded-3xl bg-slate-50 border border-slate-100 group hover:border-indigo-600 transition-all">
                  <div className="aspect-square flex items-center justify-center mb-3">
                    <SafeIcon icon={nav.icon} className={`text-3xl ${nav.color} transition-transform group-hover:scale-110`} />
                  </div>
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{nav.label}</p>
                  <p className="text-xl font-black text-slate-950 leading-none">{nav.count}</p>
                </div>
              ))}
            </div>
            
            <div className="mt-8 p-5 bg-indigo-50/50 rounded-2xl border border-indigo-100/50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-indigo-600 rounded-full animate-ping" />
                <span className="text-[10px] font-black text-indigo-900 uppercase tracking-widest">Real-time Analysis</span>
              </div>
              <SafeIcon icon={FiActivity} className="text-indigo-600" />
            </div>
          </div>
        </div>

        {/* Live Event Stream */}
        <div className="bg-white rounded-[3rem] p-10 border border-slate-100 shadow-xl">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-emerald-50 rounded-2xl text-emerald-600">
                <SafeIcon icon={FiTarget} />
              </div>
              <h3 className="text-xl font-black tracking-tight uppercase text-slate-950">Live Pulse</h3>
            </div>
            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Global Stream</span>
          </div>

          <div className="space-y-4">
            {stats.recentEvents.length > 0 ? stats.recentEvents.map((ev, i) => (
              <motion.div 
                initial={{ opacity: 0, x: -10 }} 
                animate={{ opacity: 1, x: 0 }} 
                key={ev.id || i} 
                className="flex items-center gap-4 p-4 rounded-2xl hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-100 group"
              >
                <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center text-slate-400 group-hover:bg-indigo-600 group-hover:text-white transition-all">
                  <SafeIcon icon={FiGlobe} className="text-sm" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-black text-slate-950 truncate tracking-tight">{ev.event_name}</p>
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{ev.category} • {ev.created_at ? new Date(ev.created_at).toLocaleTimeString() : 'Just now'}</p>
                </div>
                <div className="hidden sm:block">
                  <span className="px-3 py-1 bg-slate-50 text-slate-400 rounded-full text-[8px] font-black uppercase tracking-widest">Captured</span>
                </div>
              </motion.div>
            )) : (
               <div className="p-4 text-center text-slate-400 text-xs">Waiting for events...</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;